import {
  assignPrototypeAPIs,
  type CellData,
  type Column_Internal,
  type Header,
  type RowData,
  type TableFeature,
  type TableFeatures,
  columnPinningFeature,
  type Table_Internal,
} from '@tanstack/react-table';
import {
  column_getIsPinned,
  table_getColumn,
  table_getVisibleLeafColumns,
  table_setColumnSizing,
  table_setColumnResizing,
} from '@tanstack/react-table/static-functions';
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type TouchEvent as ReactTouchEvent,
} from 'react';

import type {
  ColumnSizingFeatureConstructors,
  DataTableColumnSizingState,
} from './column-sizing-types.js';
import * as styles from './ColumnSizing.sty.js';
import {
  DATA_TABLE_DEFAULT_MIN_COLUMN_WIDTH,
  DATA_TABLE_RESIZING_LEFT_POSITION_CSS_VAR,
  DATA_TABLE_RESIZING_RIGHT_POSITION_CSS_VAR,
  DATA_TABLE_STRATO_RESIZING_SIZE_CSS_VAR,
} from '../../constants.js';
import { isFractionWidth } from '../Layout/width-utils.js';
import type { TDataTableFeatures } from '../types/features.types.js';

/** Keyboard offset for column resizing (px). */
const COLUMN_RESIZE_KEYBOARD_OFFSET = 10;
/** Keyboard multiplier for column resizing when Ctrl is held. */
const COLUMN_RESIZE_KEYBOARD_CTRL_MULTIPLIER = 10;

const getClientX = (event: MouseEvent | TouchEvent): number | undefined => {
  if ('touches' in event && event.touches.length > 0) {
    return event.touches[0].clientX;
  }
  if ('changedTouches' in event && event.changedTouches.length > 0) {
    return event.changedTouches[0].clientX;
  }
  if ('clientX' in event) {
    return event.clientX;
  }
};

// Returns the width of the header cell element of the given resize handle.
function getHeaderCellWidth(resizeHandle: HTMLElement): number | undefined {
  return resizeHandle.parentElement?.getBoundingClientRect().width;
}

// helper function to clear the focus on the resizer.
function clearResizerFocus(): void {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
}

/**
 * Side effect function to add the temporary variables to pinned columns while resizing
 */
function addPinnedColumnsPositionsStyles<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  table: Table_Internal<TFeatures, TData>,
  pinnedPosition: 'left' | 'right',
  container: HTMLElement | null,
  colId: string,
  newPosition: number,
): void {
  const resizingPositionVariable =
    pinnedPosition === 'left'
      ? DATA_TABLE_RESIZING_LEFT_POSITION_CSS_VAR
      : DATA_TABLE_RESIZING_RIGHT_POSITION_CSS_VAR;

  // Update header position
  const targetColumn: HTMLElement | null | undefined = container?.querySelector(
    `[role=columnheader][data-headerid=${CSS.escape(colId)}]`,
  );
  targetColumn?.style.setProperty(resizingPositionVariable, `${newPosition}px`);

  const targetColumnParentId = table.getColumn(colId)?.parent?.id;

  if (targetColumnParentId) {
    const pinnedState = table.atoms.columnPinning?.get();
    const pinnedColumns: string[] | undefined =
      pinnedPosition === 'left' ? pinnedState?.left : pinnedState?.right;

    const pinnedChildren: string[] | undefined = pinnedColumns?.filter((id) =>
      id.includes(targetColumnParentId),
    );

    // for the left pinned columns, only update the parent position if the child at the left edge is being resized
    // for the right pinned columns, only update the parent position if the child at the right edge is being resized
    const edgeChildId: string | undefined =
      pinnedPosition === 'left'
        ? pinnedChildren?.at(0)
        : pinnedChildren?.at(-1);

    if (colId === edgeChildId) {
      const parentNode: HTMLElement | null | undefined =
        container?.querySelector(
          `[data-headerid*=${pinnedPosition}][data-headerid*=${CSS.escape(
            targetColumnParentId,
          )}]`,
        );

      parentNode?.style.setProperty(
        resizingPositionVariable,
        `${newPosition}px`,
      );
    }
  }

  // update all cells from the column
  const allColumnCells: NodeListOf<HTMLElement> | undefined =
    container?.querySelectorAll(
      `[role=cell][data-columnid=${CSS.escape(colId)}]`,
    );

  if (allColumnCells?.length) {
    Array.from(allColumnCells).forEach((cell) => {
      cell.style.setProperty(resizingPositionVariable, `${newPosition}px`);
    });
  }

  // update the position of the box-shadow
  const pinnedShadowNodes: NodeListOf<HTMLElement> | undefined =
    container?.querySelectorAll(`[data-pinned-position=${pinnedPosition}]`);

  if (pinnedShadowNodes?.length) {
    Array.from(pinnedShadowNodes).forEach((node) => {
      node.style.setProperty(resizingPositionVariable, `${newPosition}px`);
    });
  }
}

/**
 * Side effect function to clear up the container styles that have been applied while resizing.
 */
function clearContainerStyles<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  table: Table_Internal<TFeatures, TData>,
  container: HTMLElement | null,
): void {
  // Wait an animation frame to remove the custom property to prevent
  // a layout jump
  requestAnimationFrame(() => {
    container?.style.removeProperty(DATA_TABLE_STRATO_RESIZING_SIZE_CSS_VAR);
    // Remove col-resize cursor style from body element,
    // and reset pointer events on all children.
    document.body.classList.remove(styles.tableColumnResizing);

    if (table.options.enableColumnPinning) {
      container
        ?.querySelectorAll(
          `[style*=${DATA_TABLE_RESIZING_LEFT_POSITION_CSS_VAR}], [style*=${DATA_TABLE_RESIZING_RIGHT_POSITION_CSS_VAR}]`,
        )
        .forEach((elem) => {
          if (elem instanceof HTMLElement) {
            const style = elem.style;
            style.removeProperty(DATA_TABLE_RESIZING_LEFT_POSITION_CSS_VAR);
            style.removeProperty(DATA_TABLE_RESIZING_RIGHT_POSITION_CSS_VAR);
          }
        });
    }
  });
}

/**
 * Helper function to create the resize handler for a header, used in the onMouseDown and onTouchStart events of the resizer button in the header.
 * @internal
 */
export function header_getResizeHandler<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData = CellData,
>(
  header: Header<TFeatures, TData, TValue>,
): (
  event:
    | ReactMouseEvent<HTMLButtonElement>
    | ReactTouchEvent<HTMLButtonElement>,
) => void {
  const column = table_getColumn(header.column.table, header.column.id);
  const canResize = column_getCanResize(header.column);
  const pinnedPosition = column_getIsPinned(header.column);

  const isPinnedLeft = pinnedPosition === 'left';
  const isPinnedRight = pinnedPosition === 'right';

  return (
    event:
      | ReactMouseEvent<HTMLButtonElement>
      | ReactTouchEvent<HTMLButtonElement>,
  ) => {
    if (!column || !canResize) {
      return;
    }
    event.persist();

    // If a double click appears, do not continue further with the single click
    // drag handling
    if (event.detail === 2) {
      return;
    }

    const target = event.target;
    if (target instanceof HTMLElement === false) {
      return;
    }

    // store initial pinned columns widths.
    // necessary to dynamically update the positions of the pinned columns during resize
    const pinnedColumnsWidths: {
      left: Record<string, number>;
      right: Record<string, number>;
    } = { left: {}, right: {} };
    let columnIndex: number | undefined;

    if (pinnedPosition) {
      const pinnedState =
        // Safe cast here because the TFeature generic gets lost in the union to intersection,
        // but we know that if column pinning is enabled, the state will have the correct type.
        (
          header.table as unknown as Table_Internal<{
            columnPinningFeature: typeof columnPinningFeature;
          }>
        ).atoms.columnPinning.get();

      // The header id of the header the resize handle belongs to
      const headerId = target.parentElement?.dataset.headerid;

      columnIndex = headerId
        ? pinnedState[pinnedPosition]?.indexOf(headerId)
        : undefined;
      if (columnIndex !== undefined && pinnedState[pinnedPosition]?.length) {
        for (const element of pinnedState[pinnedPosition]) {
          pinnedColumnsWidths[pinnedPosition][element] =
            header.table.getColumnOrHeaderWidth(element);
        }
      }
    }

    const startSize = getHeaderCellWidth(target);

    // If the header could not be measured, we can't resize it
    if (startSize === undefined) {
      return;
    }

    const columnSizingStart: [string, number][] = [[column.id, startSize]];
    // Keep a reference for columns affected by the sizing.
    const newColumnSizing: DataTableColumnSizingState = {};
    // Get the starting clientX where the drag started.
    const clientX = getClientX(event.nativeEvent) ?? null;

    const {
      minWidth = DATA_TABLE_DEFAULT_MIN_COLUMN_WIDTH,
      maxWidth = Number.MAX_SAFE_INTEGER,
    } = column.columnDef;
    const size = `${Math.min(Math.max(startSize, minWidth), maxWidth)}px`;
    const container: HTMLElement | null = target.closest(
      '.strato-table-virtualization-container',
    );
    container?.style.setProperty(DATA_TABLE_STRATO_RESIZING_SIZE_CSS_VAR, size);
    // During resizing, add col-resize cursor style to body element,
    // and prevent pointer events on all children.
    // Note: needs to be an element outside the container
    // that does not have pointer-events: none.
    document.body.classList.add(styles.tableColumnResizing);

    // Updating the actual offset in the columnSizingInfo
    const updateOffset = (eventType: 'move' | 'end', clientXPos?: number) => {
      if (typeof clientXPos !== 'number') {
        return;
      }

      const old = // Safe cast here because the TFeature generic gets lost in the union to intersection,
        // but we know that if column pinning is enabled, the state will have the correct type.
        (
          header.table as unknown as Table_Internal<TableFeatures>
        ).atoms.columnResizing.get();

      const deltaOffset = clientXPos - (old?.startOffset ?? 0);
      const deltaOffsetRatio = deltaOffset / (old?.startSize ?? 0);
      // Calculate the percentage so the other columns next to it can be scaled
      // by the same fraction.
      const deltaPercentage = isPinnedRight
        ? deltaOffsetRatio
        : Math.max(deltaOffsetRatio, -0.9999999);

      columnSizingStart.forEach(([columnId, headerSize]) => {
        newColumnSizing[columnId] =
          Math.round(
            Math.max(
              isPinnedRight
                ? headerSize - headerSize * deltaPercentage
                : headerSize + headerSize * deltaPercentage,
              0,
            ) * 100,
          ) / 100;
      });
      const {
        minWidth = DATA_TABLE_DEFAULT_MIN_COLUMN_WIDTH,
        maxWidth = Number.MAX_SAFE_INTEGER,
      } = column.columnDef;
      const size = `${Math.min(
        Math.max(
          isPinnedRight ? startSize - deltaOffset : startSize + deltaOffset,
          minWidth,
        ),
        maxWidth,
      )}px`;

      // While dragging set the custom property value
      container?.style.setProperty(
        DATA_TABLE_STRATO_RESIZING_SIZE_CSS_VAR,
        size,
      );

      updatePinnedColumnsPositions(deltaOffset);

      // When dragging ends, we can update the actual widths of the columns
      if (eventType === 'end') {
        table_setColumnSizing(column.table, (old) => ({
          ...old,
          ...newColumnSizing,
        }));
      }
    };

    /**
     * Dynamically update the positions of the pinned columns while resizing
     */
    const updatePinnedColumnsPositions = (deltaOffset: number): void => {
      if (columnIndex === undefined) {
        return;
      }

      const leftPinnedColumnsWidths = pinnedColumnsWidths.left;
      const rightPinnedColumnsWidths = pinnedColumnsWidths.right;

      if (isPinnedLeft) {
        const values = Object.entries(leftPinnedColumnsWidths);
        const [, currentColumnInitialWidth] = values[columnIndex];

        // we should update only the left position of the pinned columns to the right of the one being resized
        for (let i = columnIndex + 1; i < values.length; i++) {
          const [colId] = values[i];

          // left position of a column is equal to the sum of the previous column widths + deltaOffset
          let prevColumnIndex = i - 1;
          let totalWidth = 0;

          while (prevColumnIndex >= 0) {
            const [, prevColWidth] = values[prevColumnIndex];
            totalWidth += prevColWidth;
            prevColumnIndex -= 1;
          }

          const leftPositionThreshold =
            totalWidth - currentColumnInitialWidth + minWidth;

          const newLeftPosition = Math.max(
            totalWidth + deltaOffset,
            leftPositionThreshold,
          );

          addPinnedColumnsPositionsStyles(
            column.table,
            'left',
            container,
            colId,
            newLeftPosition,
          );
        }
      }

      if (isPinnedRight) {
        const values = Object.entries(rightPinnedColumnsWidths);
        const [, currentColumnInitialWidth] = values[columnIndex];

        // we should update only the right position of the pinned columns to the left of the one being resized
        for (let i = columnIndex - 1; i >= 0; i--) {
          const [colId] = values[i];

          // right position of a column is equal to the next column width from the right - deltaOffset
          let prevColumnIndex = i + 1;
          let totalWidth = 0;

          while (prevColumnIndex < values.length) {
            const [, prevWidth] = values[prevColumnIndex];
            totalWidth += prevWidth;
            prevColumnIndex += 1;
          }

          const rightPositionThreshold =
            totalWidth - currentColumnInitialWidth + minWidth;

          const newRightPosition = Math.max(
            totalWidth - deltaOffset,
            rightPositionThreshold,
          );

          addPinnedColumnsPositionsStyles(
            column.table,
            'right',
            container,
            colId,
            newRightPosition,
          );
        }
      }
    };

    const onEnd = (clientXPos?: number) => {
      updateOffset('end', clientXPos);
      clearContainerStyles(column.table, container);

      // If we are coming from mouse movements, then clear the focus on resizer
      clearResizerFocus();
      table_setColumnResizing(column.table, (old) => ({
        ...old,
        isResizingColumn: false,
        startOffset: null,
        startSize: null,
        deltaOffset: null,
        deltaPercentage: null,
        columnSizingStart: [],
      }));
    };

    const moveHandler = (moveEvent: MouseEvent | TouchEvent): void => {
      const clientXPos = getClientX(moveEvent);
      updateOffset('move', clientXPos);
    };

    const upHandler = (upEvent: MouseEvent | TouchEvent): void => {
      document.removeEventListener('mousemove', moveHandler);
      document.removeEventListener('mouseup', upHandler);
      document.removeEventListener('touchmove', moveHandler);
      document.removeEventListener('touchend', upHandler);
      const clientXPos = getClientX(upEvent);
      onEnd(clientXPos);
    };

    document.addEventListener('mousemove', moveHandler);
    document.addEventListener('mouseup', upHandler);
    document.addEventListener('touchmove', moveHandler);
    document.addEventListener('touchend', upHandler);

    // If the table has any fractional templates, we need to make sure to lock their size in place, before starting
    // to resize the remaining things.
    if (header.table.getHasFractionalTemplate()) {
      const fractionColumns = header.table
        .getVisibleLeafColumnsWithoutBuiltin()
        .filter(
          ({ id, columnDef }) =>
            (columnDef.width === undefined ||
              isFractionWidth(columnDef.width)) &&
            id !== column.id,
        );

      table_setColumnSizing(header.column.table, (old) => {
        const newColumnSizing = { ...old };
        for (const fractionColumn of fractionColumns) {
          newColumnSizing[fractionColumn.id] =
            header.table.getColumnOrHeaderWidth(fractionColumn.id);
        }
        return newColumnSizing;
      });
    }

    table_setColumnResizing(header.column.table, (old) => ({
      ...old,
      startOffset: clientX,
      startSize,
      deltaOffset: 0,
      deltaPercentage: 0,
      columnSizingStart,
      isResizingColumn: column.id,
    }));
  };
}

/**
 *
 * @internal
 */
export function header_getResizeExpandHandler<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData = CellData,
>(
  header: Header<TFeatures, TData, TValue>,
): (event: ReactMouseEvent<HTMLButtonElement>) => void {
  const column = table_getColumn<TFeatures, TData>(
    header.column.table,
    header.column.id,
  );
  const canResize = column_getCanResize(header.column);

  return (event: ReactMouseEvent<HTMLButtonElement>) => {
    if (event.type !== 'dblclick') {
      return;
    }

    if (!column || !canResize) {
      return;
    }

    event.persist();

    const target = event.target;
    if (target instanceof HTMLElement === false) {
      return;
    }

    const startSize = getHeaderCellWidth(target);

    // If the header could not be measured, we can't resize it
    if (startSize === undefined) {
      return;
    }

    // Get the starting clientX where the drag started.
    const clientX = event.clientX;

    const columnSizingStart: [string, number][] = [[column.id, startSize]];
    const container: HTMLElement | null = target.closest(
      '.strato-table-virtualization-container',
    );
    // While dragging set the custom property value
    container?.style.setProperty(
      DATA_TABLE_STRATO_RESIZING_SIZE_CSS_VAR,
      'max-content',
    );

    table_setColumnResizing(header.column.table, (old) => ({
      ...old,
      startOffset: clientX,
      startSize,
      deltaOffset: 0,
      deltaPercentage: 0,
      columnSizingStart,
      isResizingColumn: column.id,
    }));

    queueMicrotask(() => {
      const target = event.target;
      if (!(target instanceof HTMLButtonElement)) {
        return;
      }
      // The button is now inside the columnheader div, so parentElement is the cell itself
      const cell: HTMLElement | null = target.parentElement;
      if (cell) {
        const { width } = cell.getBoundingClientRect();

        table_setColumnResizing(header.column.table, (old) => ({
          ...old,
          isResizingColumn: false,
          startOffset: null,
          startSize: null,
          deltaOffset: null,
          deltaPercentage: null,
          columnSizingStart: [],
        }));
        table_setColumnSizing(header.column.table, (old) => ({
          ...old,
          [column.id]: width,
        }));
        clearResizerFocus();
      }
      requestAnimationFrame(() =>
        container?.style.removeProperty(
          DATA_TABLE_STRATO_RESIZING_SIZE_CSS_VAR,
        ),
      );
    });
  };
}

/**
 * Helper function to create the resize expand handler for a header, used in the onDoubleClick event of the resizer button in the header.
 * @internal
 */
export function header_getKeyboardResizeHandler(
  header: Header<TDataTableFeatures, RowData, CellData>,
): (event: ReactKeyboardEvent<HTMLButtonElement>) => void {
  const column = table_getColumn(header.column.table, header.column.id);
  const canResize = column_getCanResize(header.column);

  return (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    const target = event.target;
    if (
      !column ||
      !canResize ||
      target instanceof HTMLElement === false ||
      (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')
    ) {
      // clear the focus on resizer
      if (event.key === 'Escape') {
        clearResizerFocus();
      }
      return;
    }

    event.persist();

    const startSize = getHeaderCellWidth(target);

    // If the header could not be measured, we can't resize it
    if (startSize === undefined) {
      return;
    }

    let offset = 0;
    if (event.key === 'ArrowLeft') {
      offset = -COLUMN_RESIZE_KEYBOARD_OFFSET;
    } else if (event.key === 'ArrowRight') {
      offset = COLUMN_RESIZE_KEYBOARD_OFFSET;
    }

    if (event.ctrlKey === true) {
      offset *= COLUMN_RESIZE_KEYBOARD_CTRL_MULTIPLIER;
    }

    table_setColumnSizing(header.column.table, (old) => ({
      ...old,
      [column.id]: startSize + offset,
    }));
  };
}

/**
 * Helper function to determine if a column can be resized, based on the table and column options, and whether it is a leaf column.
 * @internal
 */
export function column_getCanResize<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData = CellData,
>(column: Column_Internal<TFeatures, TData, TValue>) {
  // Parent columns are not resizable, only their children are.
  if (column.columns.length !== 0) {
    return false;
  }

  // columnResizing is disabled on the DataTable or on the individual column
  if (
    column.table.options.enableColumnResizing !== true ||
    column.columnDef.enableResizing === false
  ) {
    return false;
  }

  // Need to evaluate that one here, as it is not yet available in the
  // createColumn wrapper.
  const lastLeafColumn = table_getVisibleLeafColumns(column.table).at(-1);
  // Check if it is the last column
  if (column.id === lastLeafColumn?.id) {
    return false;
  }

  return true;
}

/**
 * Constructs the column sizing feature for the table,
 * including API implementations and prototype assignments.
 * @internal
 */
export function constructColumnSizingFeature(): TableFeature<ColumnSizingFeatureConstructors> {
  return {
    assignColumnPrototype: (prototype, table) => {
      assignPrototypeAPIs('columnSizingFeature', prototype, table, {
        column_getCanResize: {
          fn: (column) => column_getCanResize(column),
        },
      });
    },
    assignHeaderPrototype: (prototype, table) => {
      assignPrototypeAPIs('columnSizingFeature', prototype, table, {
        header_getResizeHandler: {
          fn: (header) => header_getResizeHandler(header),
        },
        header_getResizeExpandHandler: {
          fn: (header) => header_getResizeExpandHandler(header),
        },
        header_getKeyboardResizeHandler: {
          fn: (header) => header_getKeyboardResizeHandler(header),
        },
      });
    },
  };
}

/**
 * Additional feature implementation for column sizing.
 * @internal
 */
export const DataTableColumnSizingFeature = constructColumnSizingFeature();
